/*
 * Threshold-bar i kurven (CRO 01-10-2026).
 * Bygget efter Pepas' bar i Dawn, tilpasset Reformation:
 * kurven tegnes forfra efter hver aendring (renderContents / updateQuantity /
 * refresh i app.js), saa <cart-threshold-bar> forbindes igen hver gang og
 * afstemmer gaverne. Efter en egen aendring sendes temaets 'cart:refresh'.
 *
 * Gave-linjer kendes paa egenskaben _threshold_gift_tier (skjult i kurven,
 * fordi den starter med _).
 */
(function () {
  if (customElements.get('cart-threshold-bar')) return;

  let busy = false;
  const routes = (window.theme && window.theme.routes) || {};
  const url = (key, fallback) => (routes[key] || fallback).replace(/\.js$/, '') + '.js';

  const post = (endpoint, body) =>
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))));

  const addGift = (variantId, tierIndex) =>
    post(url('cart_add_url', '/cart/add'), {
      items: [{ id: Number(variantId), quantity: 1, properties: { _threshold_gift_tier: String(tierIndex) } }]
    });

  const removeLine = (key) => post(url('cart_change_url', '/cart/change'), { id: key, quantity: 0 });

  const refreshCart = () => document.dispatchEvent(new CustomEvent('cart:refresh'));

  class CartThresholdBar extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.dataset.config || '{"tiers":[]}');
      } catch (e) {
        this.config = { tiers: [] };
      }
      this.bindGiftForm();
      this.reconcile();
    }

    async reconcile() {
      if (busy) return;
      busy = true;
      let changed = false;
      try {
        const cart = await fetch(url('cart_url', '/cart')).then((r) => r.json());
        const tiers = this.config.tiers || [];
        const tierByIndex = new Map(tiers.map((t) => [String(t.index), t]));
        const giftLines = new Map();
        const strayGifts = [];
        // Alle produkter i kurven (koebt eller gave): en gave tilfoejes ikke, hvis
        // produktet allerede er der - Shopify-rabatten goer det saa gratis.
        const productsInCart = new Set((cart.items || []).map((l) => l.product_id));

        for (const line of cart.items || []) {
          const marker = line.properties && line.properties._threshold_gift_tier;
          if (marker) {
            giftLines.set(String(marker), line);
          } else {
            // Gave-produkt lagt i kurven uden om baren (fx via link): hoerer til det trin, hvis produkt det er
            const owner = tiers.find((t) => (t.product_ids || []).includes(line.product_id));
            if (owner && !giftLines.has(String(owner.index))) giftLines.set(String(owner.index), line);
            else if (owner) strayGifts.push(line);
            // Gave-produkt uden aktivt trin (fx uden for Danmark eller trin slaaet fra)
            else if ((this.config.gift_ids || []).includes(line.product_id)) strayGifts.push(line);
          }
        }

        // Fjern gaver, hvis trinnet ikke er naaet (eller ikke findes, fx uden for DK)
        for (const [key, line] of giftLines) {
          const tier = tierByIndex.get(key);
          if (!tier || tier.allow === false || tier.total_cents < tier.amount_cents) {
            await removeLine(line.key);
            giftLines.delete(key);
            changed = true;
          }
        }

        for (const line of strayGifts) {
          await removeLine(line.key);
          changed = true;
        }

        // Tilfoej gaver med kun en variant automatisk
        for (const tier of tiers) {
          if (!tier.variant_id || tier.allow === false || tier.total_cents < tier.amount_cents) continue;
          if (giftLines.has(String(tier.index))) continue;
          if ((tier.product_ids || []).some((id) => productsInCart.has(id))) continue;
          try {
            await addGift(tier.variant_id, tier.index);
            changed = true;
          } catch (e) {
            console.warn('[threshold-bar] kunne ikke tilfoeje gave', e);
          }
        }
      } catch (e) {
        console.error('[threshold-bar] afstemning fejlede', e);
      } finally {
        busy = false;
      }
      if (changed) refreshCart();
    }

    bindGiftForm() {
      const card = this.querySelector('[data-threshold-gift-card]');
      if (!card) return;
      let products = [];
      try {
        products = JSON.parse(card.dataset.products || '[]');
      } catch (e) {
        return;
      }
      const form = card.querySelector('[data-threshold-gift-form]');
      const productField = card.querySelector('[data-threshold-gift-product]');
      const variantSelect = card.querySelector('[data-threshold-gift-variant]');
      const image = card.querySelector('[data-threshold-gift-image]');
      const submit = card.querySelector('[data-threshold-gift-submit]');

      const renderVariants = () => {
        const product = products.find((p) => String(p.id) === String(productField.value));
        if (!product) return;
        variantSelect.innerHTML = '';
        let firstAvailable = null;
        for (const v of product.variants) {
          const opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = v.title;
          opt.disabled = !v.available;
          if (v.available && !firstAvailable) firstAvailable = v;
          variantSelect.appendChild(opt);
        }
        if (firstAvailable) variantSelect.value = firstAvailable.id;
        variantSelect.hidden = product.variants.length === 1 && product.variants[0].title === 'Default Title';
        if (image && product.image) image.src = product.image;
      };

      if (productField.tagName === 'SELECT') productField.addEventListener('change', renderVariants);
      renderVariants();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (busy || !variantSelect.value) return;
        busy = true;
        submit.disabled = true;
        submit.classList.add('loading');
        try {
          await addGift(variantSelect.value, card.dataset.tierIndex);
          busy = false;
          refreshCart();
        } catch (e) {
          console.error('[threshold-bar] kunne ikke tilfoeje valgt gave', e);
          busy = false;
          submit.disabled = false;
          submit.classList.remove('loading');
        }
      });
    }
  }

  customElements.define('cart-threshold-bar', CartThresholdBar);
})();
